import React, { useState, useEffect, useRef } from 'react';
import { ShoppingBag, Tag, Users, Headphones } from 'lucide-react';
import Navbar from './components/ui/navbar';
import './AboutPage.css';

interface AboutSection {
  id: number;
  title: string;
  description: string;
  image: string;
}

const aboutSections: AboutSection[] = [
  {
    id: 1,
    title: "Our Vision",
    description: "We envision a marketplace where quality meets innovation. Our platform connects sellers and buyers in a seamless, trustworthy environment built on cutting-edge technology.",
    image: "/home.png"
  },
  {
    id: 2,
    title: "Our Mission",
    description: "To empower sellers with the tools they need to succeed and provide buyers with an unparalleled shopping experience. We believe in transparency, quality, and customer satisfaction.",
    image: "/loginimage.jpg"
  },
  {
    id: 3,
    title: "Our Technology",
    description: "Built on a modern microservices architecture, our platform ensures scalability, security, and performance. We leverage the latest technologies to deliver a smooth experience.",
    image: "/loginimage2.jpg"
  },
  {
    id: 4,
    title: "Our Community",
    description: "Join thousands of sellers and buyers who trust our platform. We're building more than a marketplace – we're creating a community of passionate entrepreneurs and informed consumers.",
    image: "/sellerlogin.jpg"
  }
];

const AboutPage: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef(0);

  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Fleait logo",
      title: "Fleait"
    },
    menu: [
      { title: "Home", url: "/" },
      { title: "Products", url: "/products" },
      {
        title: "Categories",
        url: "#",
        items: [
          {
            title: "Fashion & Accessories",
            description: "Luxury bags, watches, jewelry, and fashion items",
            icon: <ShoppingBag className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Premium Brands",
            description: "Curated selection of high-end designer brands",
            icon: <Tag className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "Services",
        url: "#",
        items: [
          {
            title: "Personal Shopping",
            description: "Get personalized recommendations from our experts",
            icon: <Users className="size-5 shrink-0" />,
            url: "#",
          },
          {
            title: "Customer Support",
            description: "24/7 support for all your luxury shopping needs",
            icon: <Headphones className="size-5 shrink-0" />,
            url: "#",
          },
        ],
      },
      {
        title: "About",
        url: "/about",
      },
      {
        title: "Contact",
        url: "#",
      },
    ],
    mobileExtraLinks: [
      { name: "Gift Cards", url: "#" },
      { name: "Size Guide", url: "#" },
      { name: "Returns", url: "#" },
      { name: "Shipping", url: "#" },
    ],
    auth: {
      login: { text: "Sign In", url: "/signin" },
      signup: { text: "Create Space", url: "/create-space" },
    },
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const now = Date.now();

      // Throttle scroll events to prevent rapid changes
      if (now - lastScrollTime.current < 800) {
        return;
      }

      lastScrollTime.current = now;

      if (e.deltaY > 0) {
        // Scroll down
        setCurrentIndex((prev) => Math.min(prev + 1, aboutSections.length - 1));
      } else {
        // Scroll up
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  // Touch support for mobile swipe
  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
    let isSwiping = false;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      isSwiping = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping) {
        touchEndY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isSwiping) {
        return;
      }

      touchEndY = e.changedTouches[0].clientY;
      const now = Date.now();

      if (now - lastScrollTime.current < 800) {
        return;
      }

      const diff = touchStartY - touchEndY;

      if (Math.abs(diff) > 50) {
        isSwiping = true;
        lastScrollTime.current = now;

        if (diff > 0) {
          // Swipe up
          setCurrentIndex((prev) => Math.min(prev + 1, aboutSections.length - 1));
        } else {
          // Swipe down
          setCurrentIndex((prev) => Math.max(prev - 1, 0));
        }

        setTimeout(() => {
          isSwiping = false;
        }, 600);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, []);

  return (
    <div className="about-page" ref={containerRef}>
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-3 sm:pt-6 px-2 sm:px-4 pointer-events-none animate-navbar-float">
        <div className="max-w-7xl w-full bg-[rgb(255, 255, 255)] border-2 border-[rgb(0, 0, 0)] rounded-[20px] sm:rounded-[40px] px-3 sm:px-8 pointer-events-auto transition-all duration-500 hover:scale-[1.01] relative overflow-hidden before:absolute before:inset-0 before:z-[-1] before:opacity-60 before:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]">
          <Navbar {...navbarConfig} />
        </div>
      </div>

      <div className="about-container">
        {/* Left Card - Text Content */}
        <div className="about-left-card">
          <div className="content-wrapper">
            {aboutSections.map((section, index) => (
              <div
                key={section.id}
                className={`text-content ${
                  index === currentIndex ? 'active' : ''
                } ${index < currentIndex ? 'exited' : ''}`}
              >
                <h1 className="about-title">{section.title}</h1>
                <p className="about-description">{section.description}</p>
                {/* <div className="section-indicator">
                  {aboutSections.map((_, idx) => (
                    <span
                      key={idx}
                      className={`indicator-dot ${idx === currentIndex ? 'active' : ''}`}
                    />
                  ))}
                </div> */}
              </div>
            ))}
          </div>
        </div>

        {/* Right Card - Images */}
        <div className="about-right-card">
          <div className="image-wrapper">
            {aboutSections.map((section, index) => (
              <div
                key={section.id}
                className={`image-content ${
                  index === currentIndex ? 'active' : ''
                }`}
              >
                <img
                  src={section.image}
                  alt={section.title}
                  className="about-image"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Hint
      {currentIndex === 0 && (
        <div className="scroll-hint">
          <p>Scroll to explore</p>
          <div className="scroll-arrow">↓</div>
        </div>
      )} */}
    </div>
  );
};

export default AboutPage;
